import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Button,
  Card,
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  Description,
  VerifiedUser,
  AutoAwesome,
  Download,
  Delete,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  InsertDriveFile,
  Folder,
} from '@mui/icons-material';
import clientService from '../../services/clientService';
import { axelionColors } from '../../theme/axelionTheme';

const DocumentsPageGlass = () => {
  const navigate = useNavigate();

  // State management
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [aiCheckLoading, setAiCheckLoading] = useState(false);
  const [aiCheckResult, setAiCheckResult] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch documents on mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      const data = await clientService.documents.getDocuments();
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      showSnackbar('Ошибка при загрузке документов', 'error');
      // Set mock data as fallback
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showSnackbar('Файл слишком большой. Максимальный размер: 10MB', 'error');
        return;
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];

      if (!allowedTypes.includes(file.type)) {
        showSnackbar('Неподдерживаемый формат файла. Используйте PDF, DOC или DOCX', 'error');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      showSnackbar('Пожалуйста, выберите файл', 'error');
      return;
    }

    try {
      setUploadDialogOpen(false);
      setIsUploading(true);
      setUploadProgress(0);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const metadata = {
        name: selectedFile.name,
        type: selectedFile.type,
        size: selectedFile.size,
        uploadDate: new Date().toISOString(),
      };

      const result = await clientService.documents.uploadDocument(selectedFile, metadata);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Wait a bit to show 100% completion
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setSelectedFile(null);
        showSnackbar('Документ успешно загружен!', 'success');
        fetchDocuments(); // Refresh the list
      }, 500);

    } catch (error) {
      console.error('Error uploading document:', error);
      setIsUploading(false);
      setUploadProgress(0);
      showSnackbar(
        error.response?.data?.message || 'Ошибка при загрузке документа',
        'error'
      );
    }
  };

  const handleDeleteDocument = async () => {
    if (!selectedDocument) return;

    try {
      await clientService.documents.deleteDocument(selectedDocument.id);
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
      showSnackbar('Документ успешно удален!', 'success');
      fetchDocuments(); // Refresh the list
    } catch (error) {
      console.error('Error deleting document:', error);
      showSnackbar(
        error.response?.data?.message || 'Ошибка при удалении документа',
        'error'
      );
    }
  };

  const handleAICheck = async (document) => {
    try {
      setSelectedDocument(document);
      setAiDialogOpen(true);
      setAiCheckLoading(true);
      setAiCheckResult(null);

      const result = await clientService.documents.checkDocument(document.id);
      setAiCheckResult(result);
    } catch (error) {
      console.error('Error checking document:', error);
      showSnackbar(
        error.response?.data?.message || 'Ошибка при проверке документа',
        'error'
      );
      setAiDialogOpen(false);
    } finally {
      setAiCheckLoading(false);
    }
  };

  const handleDownload = (document) => {
    // In a real implementation, this would download the file
    console.log('Downloading document:', document);
    showSnackbar(`Загрузка "${document.filename || document.name}" начата`, 'info');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'checked':
        return <CheckCircle sx={{ color: axelionColors.success }} />;
      case 'warning':
        return <Warning sx={{ color: axelionColors.warning }} />;
      case 'error':
      case 'failed':
        return <ErrorIcon sx={{ color: axelionColors.error }} />;
      default:
        return <Folder sx={{ color: axelionColors.textMuted }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
      case 'checked':
        return axelionColors.success;
      case 'warning':
        return axelionColors.warning;
      case 'error':
      case 'failed':
        return axelionColors.error;
      default:
        return axelionColors.textMuted;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'verified':
      case 'checked':
        return 'Проверен';
      case 'warning':
        return 'Внимание';
      case 'error':
      case 'failed':
        return 'Ошибки';
      case 'pending':
        return 'В обработке';
      default:
        return 'Не проверен';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: axelionColors.bgCream,
        pb: 4,
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: axelionColors.bgLight, borderBottom: `1px solid ${axelionColors.borderLight}`, py: 3, px: 2 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/dashboard')}
              sx={{
                border: `1px solid ${axelionColors.borderLight}`,
                color: axelionColors.textDark,
                '&:hover': {
                  bgcolor: axelionColors.bgCream,
                  borderColor: axelionColors.gold,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 300,
                  color: axelionColors.textDark,
                  letterSpacing: '0.05em',
                  mb: 0.5,
                }}
              >
                Мои документы
              </Typography>
              <Typography variant="body1" sx={{ color: axelionColors.textMuted }}>
                Управляйте документами и проверяйте их с помощью AI
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{
                bgcolor: axelionColors.gold,
                color: '#FFFFFF',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 500,
                px: 3,
                py: 1.25,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: axelionColors.goldDark,
                  boxShadow: 'none',
                },
              }}
            >
              Загрузить
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* Upload Progress */}
        {isUploading && (
          <Card
            sx={{
              mb: 3,
              p: 3,
              bgcolor: axelionColors.bgLight,
              border: `1px solid ${axelionColors.borderLight}`,
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(26, 26, 26, 0.06)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <AutoAwesome sx={{ color: axelionColors.gold, fontSize: 32 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5, color: axelionColors.textDark }}>
                  Загрузка документа...
                </Typography>
                <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                  {uploadProgress}% - Загрузка и анализ документа
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{
                height: 8,
                borderRadius: '8px',
                bgcolor: axelionColors.borderLight,
                '& .MuiLinearProgress-bar': {
                  bgcolor: axelionColors.gold,
                  borderRadius: '8px',
                },
              }}
            />
          </Card>
        )}

        {/* Loading State */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress sx={{ color: axelionColors.gold }} size={60} />
          </Box>
        ) : documents.length === 0 ? (
          // Empty State
          <Card
            sx={{
              textAlign: 'center',
              py: 8,
              px: 4,
              bgcolor: axelionColors.bgLight,
              border: `1px solid ${axelionColors.borderLight}`,
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(26, 26, 26, 0.06)',
            }}
          >
            <Description sx={{ fontSize: 80, color: axelionColors.borderLight, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 300, color: axelionColors.textDark, letterSpacing: '0.05em' }} gutterBottom>
              Нет документов
            </Typography>
            <Typography variant="body1" sx={{ color: axelionColors.textMuted, mb: 3 }}>
              Загрузите первый документ для начала работы
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setUploadDialogOpen(true)}
              sx={{
                bgcolor: axelionColors.gold,
                color: '#FFFFFF',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 500,
                px: 3,
                py: 1.25,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: axelionColors.goldDark,
                  boxShadow: 'none',
                },
              }}
            >
              Загрузить документ
            </Button>
          </Card>
        ) : (
          // Documents Grid
          <Grid container spacing={3}>
            {documents.map((doc) => (
              <Grid item xs={12} md={6} lg={4} key={doc.id}>
                <Card
                  sx={{
                    p: 3,
                    bgcolor: axelionColors.bgLight,
                    border: `1px solid ${axelionColors.borderLight}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(26, 26, 26, 0.06)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(26, 26, 26, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '8px',
                        bgcolor: axelionColors.gold,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      <Description sx={{ fontSize: 32 }} />
                    </Box>
                    {getStatusIcon(doc.status)}
                  </Box>

                  <Tooltip title={doc.filename || doc.name}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        color: axelionColors.textDark,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 1,
                      }}
                    >
                      {doc.filename || doc.name}
                    </Typography>
                  </Tooltip>

                  {doc.type && (
                    <Chip
                      label={doc.type}
                      size="small"
                      sx={{
                        mb: 2,
                        bgcolor: axelionColors.bgWarm,
                        color: axelionColors.gold,
                        border: `1px solid ${axelionColors.borderLight}`,
                        fontWeight: 500,
                      }}
                    />
                  )}

                  {/* AI Score */}
                  {doc.aiScore !== undefined && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                          AI оценка:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 500, color: getStatusColor(doc.status) }}
                        >
                          {doc.aiScore}% - {getStatusLabel(doc.status)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={doc.aiScore}
                        sx={{
                          height: 6,
                          borderRadius: '8px',
                          bgcolor: axelionColors.borderLight,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getStatusColor(doc.status),
                            borderRadius: '8px',
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* File Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                      Размер: {formatFileSize(doc.size)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                      {formatDate(doc.uploadDate || doc.createdAt)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2, borderColor: axelionColors.borderLight }} />

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Tooltip title="Скачать">
                      <IconButton
                        size="small"
                        sx={{
                          color: axelionColors.gold,
                          border: `1px solid ${axelionColors.borderLight}`,
                          '&:hover': {
                            bgcolor: axelionColors.bgWarm,
                            borderColor: axelionColors.gold,
                          },
                        }}
                        onClick={() => handleDownload(doc)}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <IconButton
                        size="small"
                        sx={{
                          color: axelionColors.error,
                          border: `1px solid ${axelionColors.borderLight}`,
                          '&:hover': {
                            bgcolor: axelionColors.errorLight,
                            borderColor: axelionColors.error,
                          },
                        }}
                        onClick={() => {
                          setSelectedDocument(doc);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<VerifiedUser />}
                      onClick={() => handleAICheck(doc)}
                      sx={{
                        ml: 'auto',
                        bgcolor: axelionColors.gold,
                        color: '#FFFFFF',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontWeight: 500,
                        borderRadius: '8px',
                        px: 2,
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: axelionColors.goldDark,
                          boxShadow: 'none',
                        },
                      }}
                    >
                      AI проверка
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            bgcolor: axelionColors.bgLight,
            boxShadow: '0 8px 32px rgba(26, 26, 26, 0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CloudUpload sx={{ color: axelionColors.gold, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 300, color: axelionColors.textDark, letterSpacing: '0.05em' }}>
              Загрузка документа
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <input
              accept=".pdf,.doc,.docx"
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<InsertDriveFile />}
                sx={{
                  py: 4,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: axelionColors.gold,
                  color: axelionColors.gold,
                  fontSize: '1rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: axelionColors.goldDark,
                    bgcolor: axelionColors.bgWarm,
                  },
                }}
              >
                {selectedFile
                  ? selectedFile.name
                  : 'Выберите файл (PDF, DOC, DOCX)'}
              </Button>
            </label>
            {selectedFile && (
              <Alert severity="success" sx={{ mt: 2, borderRadius: '8px', bgcolor: axelionColors.successLight }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Файл выбран: {selectedFile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                  Размер: {formatFileSize(selectedFile.size)}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedFile(null);
            }}
            sx={{
              borderColor: axelionColors.borderLight,
              color: axelionColors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 500,
              borderRadius: '8px',
              px: 3,
              '&:hover': {
                borderColor: axelionColors.gold,
                bgcolor: axelionColors.bgCream,
              },
            }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadSubmit}
            disabled={!selectedFile}
            sx={{
              bgcolor: axelionColors.gold,
              color: '#FFFFFF',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 500,
              borderRadius: '8px',
              px: 3,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: axelionColors.goldDark,
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: axelionColors.bgBeige,
                color: axelionColors.textMuted,
              },
            }}
          >
            Загрузить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            bgcolor: axelionColors.bgLight,
            boxShadow: '0 8px 32px rgba(26, 26, 26, 0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ErrorIcon sx={{ color: axelionColors.error, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 300, color: axelionColors.textDark, letterSpacing: '0.05em' }}>
              Удалить документ?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Typography variant="body1" sx={{ color: axelionColors.textMuted }}>
              Вы уверены, что хотите удалить документ "
              {selectedDocument.filename || selectedDocument.name}"? Это действие нельзя
              отменить.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedDocument(null);
            }}
            sx={{
              borderColor: axelionColors.borderLight,
              color: axelionColors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 500,
              borderRadius: '8px',
              px: 3,
              '&:hover': {
                borderColor: axelionColors.gold,
                bgcolor: axelionColors.bgCream,
              },
            }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteDocument}
            sx={{
              bgcolor: axelionColors.error,
              color: '#FFFFFF',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 500,
              borderRadius: '8px',
              px: 3,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#B91C1C',
                boxShadow: 'none',
              },
            }}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Check Dialog */}
      <Dialog
        open={aiDialogOpen}
        onClose={() => !aiCheckLoading && setAiDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            bgcolor: axelionColors.bgLight,
            boxShadow: '0 8px 32px rgba(26, 26, 26, 0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AutoAwesome sx={{ color: axelionColors.gold, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 300, color: axelionColors.textDark, letterSpacing: '0.05em' }}>
              AI-анализ документа
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {aiCheckLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
              }}
            >
              <CircularProgress sx={{ color: axelionColors.gold, mb: 2 }} size={60} />
              <Typography variant="h6" sx={{ color: axelionColors.textDark }}>
                Анализируем документ...
              </Typography>
              <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                Это может занять несколько секунд
              </Typography>
            </Box>
          ) : aiCheckResult ? (
            <Box sx={{ pt: 2 }}>
              {/* Document Info */}
              <Card
                sx={{
                  mb: 3,
                  p: 3,
                  bgcolor: axelionColors.bgWarm,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: axelionColors.textMuted }} gutterBottom>
                  Документ:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 500, color: axelionColors.textDark }} gutterBottom>
                  {selectedDocument?.filename || selectedDocument?.name}
                </Typography>
                {aiCheckResult.score !== undefined && (
                  <Chip
                    label={`AI оценка: ${aiCheckResult.score}%`}
                    sx={{
                      bgcolor:
                        aiCheckResult.score >= 80
                          ? axelionColors.success
                          : aiCheckResult.score >= 50
                          ? axelionColors.warning
                          : axelionColors.error,
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Card>

              {/* Summary */}
              {aiCheckResult.summary && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: axelionColors.textDark }} gutterBottom>
                    Резюме
                  </Typography>
                  <Typography variant="body1" sx={{ color: axelionColors.textMuted }}>
                    {aiCheckResult.summary}
                  </Typography>
                </Box>
              )}

              {/* Issues */}
              {aiCheckResult.issues && aiCheckResult.issues.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 500, color: axelionColors.error }}
                    gutterBottom
                  >
                    Обнаруженные проблемы
                  </Typography>
                  {aiCheckResult.issues.map((issue, index) => (
                    <Alert
                      key={index}
                      severity="error"
                      sx={{ mb: 1, borderRadius: '8px' }}
                    >
                      {issue}
                    </Alert>
                  ))}
                </Box>
              )}

              {/* Suggestions */}
              {aiCheckResult.suggestions && aiCheckResult.suggestions.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 500, color: axelionColors.success }}
                    gutterBottom
                  >
                    Рекомендации
                  </Typography>
                  {aiCheckResult.suggestions.map((suggestion, index) => (
                    <Alert
                      key={index}
                      severity="success"
                      sx={{ mb: 1, borderRadius: '8px' }}
                    >
                      {suggestion}
                    </Alert>
                  ))}
                </Box>
              )}

              {/* Risk Level */}
              {aiCheckResult.risk && (
                <Alert
                  severity={
                    aiCheckResult.score >= 80
                      ? 'success'
                      : aiCheckResult.score >= 50
                      ? 'warning'
                      : 'error'
                  }
                  sx={{ borderRadius: '8px' }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Уровень риска: {aiCheckResult.risk}
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="error" sx={{ borderRadius: '8px' }}>
              Не удалось получить результаты анализа
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setAiDialogOpen(false);
              setAiCheckResult(null);
            }}
            disabled={aiCheckLoading}
            sx={{
              bgcolor: axelionColors.gold,
              color: '#FFFFFF',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 500,
              borderRadius: '8px',
              px: 3,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: axelionColors.goldDark,
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: axelionColors.bgBeige,
                color: axelionColors.textMuted,
              },
            }}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(26, 26, 26, 0.12)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentsPageGlass;
